import { Injectable, inject, signal } from "@angular/core";
import { App, AppState } from "@capacitor/app";
import { ConnectionType } from "@capacitor/network";
import { BundleInfo, CapacitorUpdater } from "@capgo/capacitor-updater";
import { AnalyticsEvent, CustomDimensions } from "@constants";
import { HcpUpdateStatus } from "@models/HcpUpdateStatus";
import { BaseService } from "@providers/BaseService";
import { AnalyticsService } from "@services/analytics.service";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { MondayAppInfo, MondayColumns, MondayService } from "./Monday.service";
import { NetworkService } from "./NetworkService";

interface CheckForNewVersion {
  // isNewerVersionAvailable: boolean;
  releaseUrl: string;
  latestVersion: string;
}
//eslint-disable-next-line
enum ConnectionTypesValues {
  WIFI = "wifi",
  CELULAR = "cellular",
  NONE = "none",
  UNKNOWN = "unknown",
}

/**
 * Updates service
 * Contain methods related with the application updates
 */
@Injectable({ providedIn: "root" })
export class UpdatesService extends BaseService {
  private isUpdateDownloaded: boolean = false;

  private networkService = inject(NetworkService);

  private analyticsService = inject(AnalyticsService);
  private mondayService = inject(MondayService);

  private updater: { bundle: BundleInfo } = null;

  private versionInfo: BundleInfo;

  private newVersion: CheckForNewVersion;

  private updateStatus$ = new BehaviorSubject<HcpUpdateStatus | null>(null);
  appStoreUpdateAvailable = signal<boolean>(false);

  updateStatus = this.updateStatus$.asObservable();

  updateAvailable = JSON.parse(localStorage.getItem("updateAvailable"));

  reset = false;

  constructor() {
    super();
    CapacitorUpdater.notifyAppReady().then((updaterInfo) => (this.updater = updaterInfo));

    App.addListener("appStateChange", async (state: AppState) => {
      if (this.platform.is("capacitor")) {
        // fall back when the app is on the background
        const nextBundle = await CapacitorUpdater.getNextBundle();
        // if (state.isActive && this.isUpdateDownloaded && localStorage.getItem("updateAvailable") === "true") {
        if (state.isActive && nextBundle?.status === "pending") {
          localStorage.setItem("updateAvailable", "false");
          await CapacitorUpdater.set(nextBundle); // app reloads, process restart
        } else if (state.isActive && !nextBundle) {
          const updateVersion = await this.getCapgoVersion();
          const appVersion = localStorage.getItem("currentHcpVersion");
          if (this.isHcpUpdate(updateVersion.latestVersion, appVersion)) {
            this.startUpdate(updateVersion);
          }
        }
      }
    });
  }

  async initUpdate() {
    this.updater = await CapacitorUpdater.notifyAppReady();

    let appVersionInfo: MondayAppInfo = null;
    let currentStoreVersion: string = localStorage.getItem("currentStoreVersion");

    const capgoVersion: CheckForNewVersion = await this.getCapgoVersion();
    const currentHcpVersion: string = capgoVersion.latestVersion;
    localStorage.setItem("currentHcpVersion", capgoVersion.latestVersion);

    if (!currentStoreVersion) {
      appVersionInfo = this.mondayService.mondayAppInfo();
      //TODO: Apply when hcp refactor
      // currentHcpVersion = appVersionInfo[MondayColumns.webVersion];
      currentStoreVersion = appVersionInfo[MondayColumns.actualVersion];
      localStorage.setItem("currentStoreVersion", currentStoreVersion);
    }

    // New versions has the latest version stored in capacitor.config.ts
    //const { native } = await CapacitorUpdater.current();

    // HCP update
    const { native, bundle } = await CapacitorUpdater.current();

    const appHcpVersion = bundle.version === "builtin" ? native : bundle.version;
    this.globalVars.strVersion = await this.utils.generateAppVersion(appHcpVersion);

    if (this.isHcpUpdate(currentHcpVersion, appHcpVersion)) {
      this.startUpdate(capgoVersion);
    }

    // STORE update
    const { version: appStoreVersion } = await this.getCurrentAppVersion();

    if (currentStoreVersion > appStoreVersion) {
      // Show Banner
      // Redirect user to the store
      console.log("UPDATER: Diferent Store version");
    }
  }

  updateLater() {
    this.updateStatus$.next(null);
  }

  async startDownload() {
    this.updateStatus$.next(null);
    await this.downloadUpdate(this.newVersion);
  }

  async installUpdate() {
    this.updateStatus$.next(null);
    try {
      localStorage.setItem("updateAvailable", "false");
      this.analyticsService.trackEvent(AnalyticsEvent.HCP_UPDATE_COMPLETE);
      const nextVersion = await CapacitorUpdater.getNextBundle();
      await CapacitorUpdater.set(nextVersion);
    } catch (error) {
      console.error("Falló la actualización", error);
      this.analyticsService.trackEvent(AnalyticsEvent.HCP_UPDATE_ERROR, { [CustomDimensions.LABEL]: error });
    }
  }

  private async getCapgoVersion(): Promise<CheckForNewVersion> {
    try {
      const { latestVersion, releaseUrl } = await firstValueFrom(
        this.httpClient.get<any>(this.globalVars.HCP_URL),
      );
      return {
        latestVersion,
        releaseUrl,
      };
    } catch (error) {
      this.analyticsService.trackEvent(AnalyticsEvent.HCP_UPDATE_ERROR, { [CustomDimensions.LABEL]: error });
      return {
        latestVersion: null,
        releaseUrl: null,
      };
    }
  }

  /**
   * Setup Updates Listernes
   * we monitor the progress, when finished and any failures
   */
  private async startUpdatesListeners() {
    await CapacitorUpdater.addListener("download", (e) => {
      console.log("CapacitorUpdater Update progress::", JSON.stringify(e));
    });

    await CapacitorUpdater.addListener("downloadComplete", async (e) => {
      console.log("CapacitorUpdater Download Complete", JSON.stringify(e.bundle));
      await CapacitorUpdater.next(e.bundle);
      // only time the var is set true is when the download is completed.
      this.isUpdateDownloaded = true;
      localStorage.setItem("updateAvailable", "true");
      await CapacitorUpdater.removeAllListeners();
    });

    await CapacitorUpdater.addListener("updateFailed", (e) => {
      console.error("CapacitorUpdater Update Failed", JSON.stringify(e));
    });
  }

  /**
   * Cancel download
   */
  private async cancelUpdateDownload() {
    await CapacitorUpdater.setMultiDelay({
      delayConditions: [
        {
          kind: "background",
        },
      ],
    });
  }

  private async startUpdate(response: CheckForNewVersion) {
    // const { bundles }: BundleListResult = await CapacitorUpdater.list();
    const latestBundle = await CapacitorUpdater.getNextBundle();

    if (this.updateAvailable && latestBundle) {
      localStorage.setItem("updateAvailable", "false");
      this.analyticsService.trackEvent(AnalyticsEvent.HCP_UPDATE_COMPLETE);
      await CapacitorUpdater.set(latestBundle); // app reloads, process restart
    } else {
      const { connected, connectionType } = this.networkService.statusSignal();
      if (!connected) return;
      await this.checkConnectionType(connectionType, response);
    }
  }

  /**
   * Update Download
   * @param version CheckForNewVersion
   */
  private async downloadUpdate(version: CheckForNewVersion) {
    let newVersion: BundleInfo;
    try {
      this.analyticsService.trackEvent(AnalyticsEvent.HCP_UPDATE_START);
      // starts listeners if connected
      await this.startUpdatesListeners();

      newVersion = await CapacitorUpdater.download({
        url: version.releaseUrl,
        version: version.latestVersion,
      });
    } catch (error) {
      this.analyticsService.trackEvent(AnalyticsEvent.HCP_UPDATE_ERROR, { [CustomDimensions.LABEL]: error });
    } finally {
      if (newVersion) {
        this.isUpdateDownloaded = true;
        localStorage.setItem("updateAvailable", "true");
        localStorage.setItem("currentHcpVersion", newVersion.version);
        this.versionInfo = newVersion;
        this.updateStatus$.next(HcpUpdateStatus.Downloaded);
      }
    }
  }

  private async getCurrentAppVersion(): Promise<{ version: string; build: string }> {
    const { version, build } = await App.getInfo();
    return {
      version,
      build,
    };
  }

  private isHcpUpdate(currentHcpVersion: string, appHcpVersion: string): boolean {
    return (
      +currentHcpVersion.split(".").join("") > +appHcpVersion.split(".").join("") || appHcpVersion === "builtin"
    );
  }

  private async checkConnectionType(connectionType: ConnectionType, response: CheckForNewVersion): Promise<void> {
    switch (connectionType) {
      case ConnectionTypesValues.WIFI:
        await this.downloadUpdate(response);
        break;
      case ConnectionTypesValues.CELULAR:
        this.updateStatus$.next(HcpUpdateStatus.Available);
        this.newVersion = response;
        break;
      case ConnectionTypesValues.NONE:
      case ConnectionTypesValues.UNKNOWN:
        await this.cancelUpdateDownload();
        break;
    }
  }
}
